'use client';

/**
 * Почему пропустили.
 *
 * **Судьба и причина — разные вопросы.** Плитка пропущенных отвечает «спасли
 * ли клиента»; это — «почему потеряли». Первое про прошлое, второе про то,
 * что делать, чтобы не терять следующих. Поэтому отдельный блок, а не
 * подпись к плитке.
 *
 * Шесть непересекающихся групп, в сумме дающих `count`. У каждой — своё
 * решение, и рядом с числом стоит именно оно: «укоротить приветствие», «мало
 * людей в смене». Без этого таблица остаётся счётчиком.
 *
 * `noRoute` выделен: это не пропущенный по вине людей звонок, а поломка
 * настройки. Смешав её с остальными, руководитель будет чинить не то.
 */

import React from 'react';
import Link from 'next/link';
import { AlertTriangle, ArrowUpRight } from 'lucide-react';
import { missedReason as reasonDict } from '@/lib/dict';
import { count, percent } from '@/lib/format';
import type { MissedReason, ReportFilters } from '@/lib/api/types';

/**
 * Переход в журнал под ту же выборку.
 *
 * Две строки одним запросом сейчас не выражаются: `waiting` — это «бросил,
 * но **не** на приветствии», а фильтра-отрицания у `reached` нет; `noAnswer`
 * — это `no_answer` и `busy` вместе, а `disposition` принимает одно
 * значение. У таких переходов `exact: false`, и счётчик рядом с ними мы не
 * показываем: число в дашборде и число строк в журнале разошлись бы, и
 * объяснять это пришлось бы каждому, кто заметит.
 */
const links: Record<keyof MissedReason, { query: Record<string, string>; exact: boolean } | null> =
  {
    greeting: { query: { reached: 'ivr', disposition: 'abandoned' }, exact: true },
    waiting: { query: { disposition: 'abandoned' }, exact: false },
    noAnswer: { query: { disposition: 'no_answer' }, exact: false },
    noRoute: { query: { disposition: 'failed' }, exact: true },
    voicemail: { query: { disposition: 'voicemail' }, exact: true },
    unclassified: null,
  };

const order: (keyof MissedReason)[] = [
  'noRoute',
  'noAnswer',
  'waiting',
  'greeting',
  'voicemail',
  'unclassified',
];

export default function MissedReasons({
  reason,
  total,
  filters,
}: {
  reason: MissedReason;
  /** `missed.count` — знаменатель долей. */
  total: number;
  filters: ReportFilters;
}) {
  const rows = order.filter((key) => reason[key] > 0);

  if (rows.length === 0) {
    return (
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-slate-800 mb-1">Почему пропустили</h2>
        <p className="text-xs text-slate-400 mt-3">
          За период пропущенных не было — разбирать нечего.
        </p>
      </div>
    );
  }

  const href = (key: keyof MissedReason) => {
    const link = links[key];
    if (!link) return null;
    const params = new URLSearchParams(link.query);
    for (const [name, value] of Object.entries(filters)) {
      if (value) params.set(name, String(value));
    }
    return `/call-list?${params.toString()}`;
  };

  const worst = Math.max(...rows.map((key) => reason[key]));

  return (
    <div className="card p-5">
      <h2 className="text-sm font-semibold text-slate-800 mb-1">Почему пропустили</h2>
      <p className="text-xs text-slate-400 mb-4">
        Где оборвался вызов — это другой вопрос, чем «перезвонили или нет»
      </p>

      <div className="space-y-2.5">
        {rows.map((key) => {
          const term = reasonDict[key];
          const link = links[key];
          const target = href(key);
          const value = reason[key];

          const body = (
            <>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <span className="text-xs text-slate-700 truncate">
                  {key === 'noRoute' && (
                    <AlertTriangle
                      size={11}
                      className="inline text-red-500 mr-1 -mt-0.5"
                      aria-label="поломка настройки"
                    />
                  )}
                  {term.label}
                  <span className="ml-1.5 text-[10px] text-slate-400">{term.fix}</span>
                </span>
                <span className="text-xs font-semibold tabular-nums text-slate-700 flex-shrink-0 inline-flex items-center gap-1.5">
                  {count(value)}
                  {/* Долю показываем только у точных переходов: у
                      приблизительных число в журнале будет другим. */}
                  {total > 0 && link?.exact && (
                    <span className="font-normal text-[10px] text-slate-400">
                      {percent(value / total)}
                    </span>
                  )}
                  {target && <ArrowUpRight size={11} className="text-slate-300" />}
                </span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    key === 'noRoute'
                      ? 'bg-red-500'
                      : term.tone === 'warning'
                        ? 'bg-amber-400'
                        : 'bg-slate-300'
                  }`}
                  style={{ width: `${(value / worst) * 100}%` }}
                />
              </div>
            </>
          );

          return target ? (
            <Link
              key={key}
              href={target}
              className="block rounded hover:bg-slate-50 -mx-1 px-1 transition-colors"
              title={
                link?.exact
                  ? 'Открыть эти звонки в журнале'
                  : 'Открыть в журнале по близкому фильтру — точнее одним запросом не выразить'
              }
            >
              {body}
            </Link>
          ) : (
            <div key={key}>{body}</div>
          );
        })}
      </div>

      <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
        «Не дождался» и «не взяли трубку» открываются по близкому фильтру: точнее одним запросом
        журнал пока не спросить, поэтому доля у них не показана.
      </p>
    </div>
  );
}
